import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Permissionset } from 'app/shared/models/permissionset';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import {
  DialogResult,
  tr,
  DialogService,
  chainAndForkJoinObservables,
  NullEntity,
  languageMap,
  EntityDetailsGeneralBaseComponent,
} from '@kpd/framework-ngx';
import { ActivatedRoute } from '@angular/router';
import { PermissionsetsNavigatorHelper } from 'app/permissionsets/permissionsets-navigator-helper';
import { ApplicationService } from 'app/shared/services/application.service';
import { Application } from 'app/shared/models/application';
import { HttpErrorResponse } from '@angular/common/http';
import { Permissions } from 'app/shared/services/permissions';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { catchError, tap, takeUntil } from 'rxjs/operators';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { PermissionsetFacade } from 'app/shared/services/facades/permissionset.facade';
import { Observable } from 'rxjs';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-permission-set-details-general',
  templateUrl: './permissionset-details-general.component.html',
})
export class PermissionsetDetailsGeneralComponent
  extends EntityDetailsGeneralBaseComponent
  implements OnInit, OnChanges
{
  Permissions: typeof Permissions = Permissions;
  @Input()
  item: Permissionset;
  @Input()
  keyOfPermissionSetToCopy: string;
  @Input()
  canManage = true;
  permissionSetToCopy?: Permissionset;

  applications: Application[] = [];
  descriptionsFormGroup: FormGroup = new FormGroup({});
  updatingFields = false;
  userLanguage = 'dutch';
  isKpdSet: boolean;
  nameAndDescriptionColumns = [
    { name: 'name', title: tr('Name'), width: '30%' },
    { name: 'description', title: tr('Description'), width: '70%' },
  ];

  typeOptions = [
    { value: 'assign', label: tr('Assign') },
    { value: 'use', label: tr('Use') },
    { value: 'both', label: tr('Both') },
  ];
  languageFields: AbstractControl[];

  constructor(
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private permissionsetService: PermissionsetService,
    protected navigatorHelper: PermissionsetsNavigatorHelper,
    private applicationService: ApplicationService,
    private permissionSetFacade: PermissionsetFacade,
    localizationService: UMLocalizationService,
    activatedRoute: ActivatedRoute,
  ) {
    super(localizationService as any, activatedRoute);
    this.userLanguage = this.getLanguage();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.isKpdSet = this.getIsKpdSet();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && this.item) {
      this.isKpdSet = this.getIsKpdSet();
    }

    super.ngOnChanges(changes);
    if (changes.keyOfPermissionSetToCopy && this.keyOfPermissionSetToCopy && this.item.isNew()) {
      this.copyPermissionset(this.keyOfPermissionSetToCopy);
    }
  }

  getLanguage(): string {
    const langCode = this.localizationService.getLanguage();
    if (langCode) {
      return languageMap[langCode];
    }
    return 'english';
  }

  getIsKpdSet(): boolean {
    if (this.item.isKpdSet) {
      return this.item.isKpdSet;
    }
    return false;
  }

  createForm(permissionset: Permissionset = this.item, copying: boolean = false): FormGroup {
    this.descriptionsFormGroup = new FormGroup({
      dutch: new FormControl(permissionset.description ? permissionset.description.dutch : undefined, [
        Validators.maxLength(500),
      ]),
      english: new FormControl(permissionset.description ? permissionset.description.english : undefined, [
        Validators.maxLength(500),
      ]),
      french: new FormControl(permissionset.description ? permissionset.description.french : undefined, [
        Validators.maxLength(500),
      ]),
      german: new FormControl(permissionset.description ? permissionset.description.german : undefined, [
        Validators.maxLength(500),
      ]),
    });

    let name = permissionset.name;
    if (copying) {
      if (name.length <= 93) {
        name = name + ' (copy)';
      } else {
        name = name.slice(0, 93) + ' (copy)';
      }
    }

    const form = this.formBuilder.group({
      name: new FormControl(name, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(100),
        Validators.pattern(/^(?!.*\/).*$/), // No forward slash allowed
      ]),
      application: new FormControl(
        permissionset.application ? { name: permissionset.application } : undefined,
        Validators.required,
      ),
      description: new FormControl(
        permissionset.description ? permissionset.description[this.userLanguage] : undefined,
        Validators.maxLength(500),
      ),
      type: new FormControl(permissionset.type || 'use'),
      descriptions: this.descriptionsFormGroup,
    });

    if (permissionset.isNew() || copying) {
      this.applicationService.getApplicationsByTenant().subscribe((apps) => {
        this.applications = apps;
        const app = this.applications.find((application) => application.name === permissionset.application);
        form.patchValue({
          application: app,
        });
      });
    }

    form.controls.application.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (
        form.controls.name.hasError('permissionSetNameConflictGlobal') ||
        form.controls.name.hasError('permissionSetNameConflict')
      ) {
        form.controls.name.setErrors(null);
      }
    });

    this.languageFields = Object.values(this.descriptionsFormGroup.controls);

    return form;
  }

  formToEntity(): Permissionset {
    const permissionSet: Permissionset = this.item.clone();
    permissionSet.name = this.form.value.name;
    permissionSet.type = this.form.value.type;
    if (this.form.value.application) {
      permissionSet.application = this.form.value.application.name;
    }
    permissionSet.description = {};
    permissionSet.description.dutch = this.descriptionsFormGroup.value.dutch;
    permissionSet.description.english = this.descriptionsFormGroup.value.english;
    permissionSet.description.french = this.descriptionsFormGroup.value.french;
    permissionSet.description.german = this.descriptionsFormGroup.value.german;

    return permissionSet;
  }

  executeSave(form: FormGroup): void {
    const permissionSetKey = new PermissionsetKey(
      this.formToEntity().name,
      this.formToEntity().application as string,
      true,
    );
    const isNameChanged =
      ((form.get('name') as AbstractControl).dirty || this.item.isNew()) && !this.keyOfPermissionSetToCopy;

    // Setup function to save first
    let saveFunction = () => this.updater.saveEntity(this.formToEntity(), isNameChanged);

    if (this.item.isNew()) {
      // If the item is new, check if a permission with this key already exists
      // If not, it will throw a 404 error, so in catchError we can actually save the new permissionset
      saveFunction = () =>
        this.permissionsetService.getById(permissionSetKey).pipe(
          tap(() => {
            (form.get('name') as any).setErrors({ permissionSetNameConflictGlobal: true });
            this.setFieldErrors(true);
          }),
          catchError(() => {
            return this.updater.saveEntity(this.formToEntity(), isNameChanged);
          }),
        );
    }
    this.permissionSetFacade.detailLoadingStarted();
    saveFunction().subscribe(
      (result) => {
        this.form.markAsPristine();
        if (this.permissionSetToCopy) {
          const copyPermissionsets = this.copyPermissionsetPermissions(result);
          if (copyPermissionsets) {
            this.permissionSetFacade.detailLoadingStarted();
            copyPermissionsets.subscribe(
              () => {
                this.finishCopy(result);
              },
              (error) => {
                setTimeout(() => {
                  this.finishCopy(result);
                });
                throw new Error(`Copy of permissions failed. ${error}`);
              },
            );
          } else {
            this.finishCopy(result);
          }
        }
        this.permissionSetFacade.detailLoadingFinished();
      },
      (err: HttpErrorResponse) => {
        this.permissionSetFacade.detailLoadingFinished();
        if (err && err.status === 409) {
          (form.get('name') as any).setErrors({ permissionSetNameConflict: true });
          this.setFieldErrors(true);
        }
      },
    );
  }

  finishCopy(result: Permissionset): void {
    this.permissionSetToCopy = undefined;
    this.form.markAsPristine();
    this.permissionSetFacade.detailLoadingFinished();
    this.navigatorHelper.navigateToEdit(result.key, true, true);
  }

  isValid(form: FormGroup): boolean {
    if (!form.dirty) {
      return true;
    }

    if (form.invalid) {
      this.setFieldErrors(true);
      return false;
    }

    return true;
  }

  onRevert(): void {
    if (!this.form.dirty) {
      return;
    }
    this.updater.confirmAndRevert(this.item).subscribe((result: DialogResult) => {
      if (result === DialogResult.Verify) {
        this.form = this.createForm();
      }
    });
  }

  onAdd(): void {
    if (this.form.invalid) {
      this.setFieldErrors(true);
    }

    this.navigateToCreatePermissionSet();
  }

  navigateToCreatePermissionSet(): void {
    this.navigatorHelper.navigateToCreateFromDetail(false);
  }

  protected getControl(fieldName: string): AbstractControl {
    const control = this.form.get(fieldName) || this.descriptionsFormGroup.get(fieldName);
    return control as AbstractControl;
  }

  hasError(fieldName: string): boolean {
    if (this.item instanceof NullEntity || !this.item) {
      return false;
    }
    return super.hasError(fieldName);
  }

  hasTranslationSync(translation: string): AbstractControl | null {
    return this.userLanguage === translation ? this.form.controls.description : null;
  }

  copyPermissionset(keyOfPermissionSetToCopy: string): void {
    this.permissionSetFacade.detailLoadingStarted();
    const key = new PermissionsetKey(
      keyOfPermissionSetToCopy.split(':')[0],
      keyOfPermissionSetToCopy.split(':')[1],
      false,
    );
    this.permissionsetService.getById(key).subscribe(
      (permissionSetToCopy) => {
        this.permissionSetToCopy = permissionSetToCopy;
        this.form = this.createForm(permissionSetToCopy, true);
        this.form.markAsDirty();
        this.permissionSetFacade.detailLoadingFinished();
      },
      () => {
        this.permissionSetFacade.detailLoadingFinished();
      },
    );
  }

  copyPermissionsetPermissions(result: Permissionset): Observable<any[]> | null {
    if (this.permissionSetToCopy && this.permissionSetToCopy.permissions) {
      const observables: Observable<any>[] = [];
      this.permissionSetToCopy.permissions.forEach((p) => {
        observables.push(this.permissionsetService.addPermissionToPermissionSet(result.key, p.key));
      });
      return chainAndForkJoinObservables(observables);
    }
    return null;
  }

  onCopy(): void {
    this.dialogService
      .open('copyPermissionSet', this.item)
      .afterClosed()
      .subscribe((result: any) => {
        if (result) {
          this.navigatorHelper.navigateToConsult(result.key, true);
        }
      });
  }
}
